import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, Save, X } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import FormModal from '../../../components/Modals/FormModal';

import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import { RestaurantRawMaterialCategoryDTO } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';

interface Props {
  isOpen: boolean;
  categories: RestaurantRawMaterialCategoryDTO[];
  units: IProductUnit[];
  /** Nombre inicial sugerido (p. ej. el texto que se buscó en el selector). */
  initialName?: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => Promise<void>;
  onCreateCategory: (
    name: string
  ) => Promise<RestaurantRawMaterialCategoryDTO | null>;
}

const RawMaterialQuickCreateModal = ({
  isOpen,
  categories,
  units,
  initialName = '',
  saving,
  onClose,
  onSubmit,
  onCreateCategory,
}: Props) => {
  const [name, setName] = useState(initialName);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [unitId, setUnitId] = useState<number | ''>('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [touched, setTouched] = useState(false);
  const wasOpen = useRef(false);

  // Reinicia el formulario SOLO al abrir (no cuando cambian las categorías por
  // crear una al vuelo, que borraría lo que el usuario ya escribió).
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setName(initialName);
      setCategoryId(categories.length === 1 ? categories[0].id : '');
      setUnitId('');
      setShowNewCategory(categories.length === 0);
      setNewCategoryName('');
      setTouched(false);
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialName, categories]);

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Indique el nombre de la materia prima.';
    if (!categoryId) next.categoryId = 'Seleccione o cree una categoría.';
    if (!unitId) next.unitId = 'Seleccione la unidad base.';
    return next;
  }, [name, categoryId, unitId]);

  const hasErrors = Object.keys(errors).length > 0;

  const addCategory = async () => {
    const value = newCategoryName.trim();
    if (!value || creatingCategory) return;
    setCreatingCategory(true);
    try {
      const created = await onCreateCategory(value);
      if (created) {
        setCategoryId(created.id);
        setShowNewCategory(false);
        setNewCategoryName('');
      }
    } finally {
      setCreatingCategory(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (hasErrors || saving) return;
    await onSubmit({
      name: name.trim(),
      categoryId: Number(categoryId),
      unitId: Number(unitId),
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Crear materia prima"
      maxWidth="max-w-lg"
      zIndex="top"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-bodydark2 dark:text-bodydark">
          Se crea al instante con su registro de inventario. El código se genera
          automáticamente a partir de la categoría.
        </p>

        <InputField
          label="Nombre"
          name="quickRawMaterialName"
          type="text"
          iconKey="product"
          required
          value={name}
          error={touched ? errors.name : undefined}
          placeholder="Ej. Carne de res"
          onChange={(event) => setName(event.target.value)}
        />

        {!showNewCategory ? (
          <div className="space-y-1">
            <SimpleSearchSelect
              label="Categoría"
              name="quickRawMaterialCategory"
              selectedValue={categoryId || null}
              iconKey="category"
              placeholder="Seleccione una categoría"
              searchPlaceholder="Buscar categoría..."
              required
              disabled={saving}
              error={touched ? errors.categoryId : undefined}
              options={categories.map((category) => ({
                id: category.id,
                name: category.name,
              }))}
              onSelect={(selected) => setCategoryId(selected.id)}
            />
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              onClick={() => setShowNewCategory(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Nueva categoría
            </button>
          </div>
        ) : (
          <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/25 dark:bg-primary/10">
            <InputField
              label="Nueva categoría"
              name="quickRawMaterialNewCategory"
              type="text"
              iconKey="category"
              value={newCategoryName}
              placeholder="Ej. Carnes"
              onChange={(event) => setNewCategoryName(event.target.value)}
            />
            <div className="flex items-center gap-2">
              <ActionButton
                label="Agregar"
                icon={<Check className="h-4 w-4" />}
                variant="primary"
                size="sm"
                fullWidth={false}
                loading={creatingCategory}
                disabled={!newCategoryName.trim() || creatingCategory}
                onClick={addCategory}
              />
              {categories.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-bodydark2 hover:underline"
                  onClick={() => setShowNewCategory(false)}
                >
                  Usar una existente
                </button>
              )}
            </div>
          </div>
        )}

        <SimpleSearchSelect
          label="Unidad base"
          name="quickRawMaterialUnit"
          selectedValue={unitId || null}
          iconKey="unitMeasure"
          placeholder="Seleccione la unidad base"
          searchPlaceholder="Buscar unidad..."
          required
          disabled={saving}
          error={touched ? errors.unitId : undefined}
          options={units.map((unit) => ({
            id: unit.id,
            name: unit.unitCode ? `${unit.name} (${unit.unitCode})` : unit.name,
          }))}
          onSelect={(selected) => setUnitId(selected.id)}
        />

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:justify-end">
          <ActionButton
            label="Cancelar"
            icon={<X className="h-4 w-4" />}
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
            className="w-full sm:w-auto"
          />
          <ActionButton
            label="Crear y usar"
            icon={<Save className="h-4 w-4" />}
            loading={saving}
            loadingLabel="Creando..."
            variant="primary"
            size="md"
            type="submit"
            fullWidth={false}
            disabled={saving || (touched && hasErrors)}
            className="w-full whitespace-nowrap sm:w-auto"
          />
        </div>
      </form>
    </FormModal>
  );
};

export default RawMaterialQuickCreateModal;
